import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent, type ReactNode } from "react";

/**
 * กราฟวาดเป็น inline SVG เอง ไม่ใช้ไลบรารีชาร์ต
 *
 * เหตุผล: build/รันต้อง offline ได้ (กฎเดียวกับฟอนต์ใน CLAUDE.md), ชาร์ตที่จอต้องการมีไม่กี่แบบ
 * และไลบรารีอย่าง recharts กินขนาด bundle มากกว่าทั้งแอปนี้รวมกัน
 *
 * กราฟแกน x/y วัดความกว้างจริงของกล่องแล้ววาดเป็นพิกเซล (ไม่ใช้ viewBox ยืด) —
 * ไม่งั้นตัวหนังสือบนแกนจะใหญ่ตามความกว้างจอ อ่านแล้วเหมือนกราฟเด็ก
 *
 * ทุกตัวรับสีมาเป็น CSS variable (เช่น "var(--navy-700)") เพื่อไม่ให้มีสีดิบในโค้ด
 */

/** ผู้ใช้ที่ขอให้ลดการเคลื่อนไหว — ข้าม animation ทั้งหมด (CSS ก็เช็คซ้ำอีกชั้น) */
const REDUCED_MOTION =
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** ความกว้างจริงของกล่อง — ได้ค่าจาก ResizeObserver (ยิงครั้งแรกทันทีที่ observe) */
function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setWidth(Math.round(w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

/** แกน y ที่อ่านง่าย: ขั้นละ 1/2/2.5/5 × 10^n — `integer` = ค่านับ ห้ามมีขั้นทศนิยม */
function niceScale(max: number, integer: boolean, count = 4): { top: number; ticks: number[] } {
  if (max <= 0) return { top: 1, ticks: [0, 1] };
  const raw = max / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  let step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? raw;
  if (integer) step = Math.max(1, Math.ceil(step));
  const top = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= top + step / 2; v += step) ticks.push(v);
  return { top, ticks };
}

/** ตัวเลขย่อบนแกน y */
function axisNumber(v: number): string {
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toLocaleString("lo-LA", { maximumFractionDigits: 1 })} ລ້ານ`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toLocaleString("lo-LA", { maximumFractionDigits: 1 })} ພັນ`;
  return v.toLocaleString("lo-LA", { maximumFractionDigits: 1 });
}

/** ไม่มีข้อมูลต้องบอกตรง ๆ ไม่ใช่วาดกราฟเปล่าที่อ่านเหมือนยอดเป็นศูนย์ */
function Empty({ label }: { label: string }) {
  return <div className="chart__empty">{label}</div>;
}

interface TooltipRow {
  label: string;
  color: string;
  value: string;
}

function Tooltip({ x, title, rows, width }: { x: number; title: string; rows: TooltipRow[]; width: number }) {
  // พลิกไปซ้ายเมื่อใกล้ขอบขวา ไม่ให้ tooltip ล้นกล่อง
  const flip = x > width - 180;
  return (
    <div className="chart-tip" style={flip ? { right: width - x + 12 } : { left: x + 12 }} role="presentation">
      <div className="chart-tip__title">{title}</div>
      {rows.map((r) => (
        <div key={r.label} className="chart-tip__row">
          <span className="legend__swatch" style={{ background: r.color }} />
          <span className="chart-tip__label">{r.label}</span>
          <span className="chart-tip__value num">{r.value}</span>
        </div>
      ))}
    </div>
  );
}

const PAD = { top: 12, right: 12, bottom: 28, left: 56 };

/** เส้นกริด + ป้ายแกน y */
function YAxis({ ticks, top, width, plotH, format }: { ticks: number[]; top: number; width: number; plotH: number; format: (v: number) => string }) {
  return (
    <g>
      {ticks.map((t) => {
        const y = PAD.top + plotH - (t / top) * plotH;
        return (
          <g key={t}>
            <line className={t === 0 ? "chart__baseline" : "chart__grid"} x1={PAD.left} x2={width - PAD.right} y1={y} y2={y} />
            <text className="chart__axis-label" x={PAD.left - 8} y={y + 3.5} textAnchor="end">
              {format(t)}
            </text>
          </g>
        );
      })}
    </g>
  );
}

/** ป้ายแกน x — ข้ามบางตัวเมื่อช่องแคบเกิน ไม่ให้ตัวหนังสือซ้อนกัน */
function xLabelEvery(slot: number): number {
  return Math.max(1, Math.ceil(44 / Math.max(slot, 1)));
}

export interface BarSeries {
  /** ป้ายใน legend (ภาษาลาว) */
  label: string;
  color: string;
  values: number[];
}

interface BarChartProps {
  series: BarSeries[];
  /** ป้ายใต้แต่ละกลุ่ม — ความยาวต้องเท่ากับ values ของทุกซีรีส์ */
  labels: string[];
  emptyLabel: string;
  /** อธิบายกราฟให้ screen reader (ภาษาลาว) */
  title: string;
  /** แปลงค่าเป็นข้อความใน tooltip */
  format?: (v: number) => string;
  height?: number;
}

/** กราฟแท่งแบบจัดกลุ่ม */
export function BarChart({ series, labels, emptyLabel, title, format, height = 240 }: BarChartProps) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const groups = labels.length;
  const max = Math.max(0, ...series.flatMap((s) => s.values));
  const empty = groups === 0 || series.length === 0 || max === 0;
  const fmt = format ?? ((v: number) => v.toLocaleString("lo-LA"));

  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = height - PAD.top - PAD.bottom;
  const { top, ticks } = niceScale(max, false);
  const slot = groups > 0 ? plotW / groups : 0;
  const barWidth = Math.max(4, Math.min(28, (slot * 0.62) / Math.max(series.length, 1) - 2));
  const groupWidth = barWidth * series.length + 3 * (series.length - 1);
  const every = xLabelEvery(slot);

  return (
    <div ref={ref} className="chart-box">
      {empty ? (
        <Empty label={emptyLabel} />
      ) : width > 0 ? (
        <>
          <svg className="chart" width={width} height={height} role="img" aria-label={title} onMouseLeave={() => setHover(null)}>
            <YAxis ticks={ticks} top={top} width={width} plotH={plotH} format={axisNumber} />
            {labels.map((label, g) => {
              const left = PAD.left + g * slot + (slot - groupWidth) / 2;
              return (
                <g key={label + String(g)}>
                  {hover === g ? (
                    <rect className="chart__hover-band" x={PAD.left + g * slot} y={PAD.top} width={slot} height={plotH} />
                  ) : null}
                  {series.map((s, i) => {
                    const value = s.values[g] ?? 0;
                    const h = (value / top) * plotH;
                    return (
                      <rect
                        key={s.label}
                        className="chart__bar"
                        style={{ animationDelay: `${String(g * 40)}ms` }}
                        x={left + i * (barWidth + 3)}
                        y={PAD.top + plotH - h}
                        width={barWidth}
                        height={Math.max(h, value > 0 ? 2 : 0)}
                        rx={Math.min(5, barWidth / 3)}
                        fill={s.color}
                      />
                    );
                  })}
                  {g % every === 0 ? (
                    <text className="chart__axis-label" x={PAD.left + g * slot + slot / 2} y={height - 8} textAnchor="middle">
                      {label}
                    </text>
                  ) : null}
                  {/* แถบโปร่งทั้งคอลัมน์รับเมาส์ — แท่งเตี้ยก็ชี้โดนง่าย */}
                  <rect
                    x={PAD.left + g * slot}
                    y={PAD.top}
                    width={slot}
                    height={plotH}
                    fill="transparent"
                    onMouseEnter={() => setHover(g)}
                  />
                </g>
              );
            })}
          </svg>
          {hover !== null ? (
            <Tooltip
              x={PAD.left + hover * slot + slot / 2}
              width={width}
              title={labels[hover] ?? ""}
              rows={series.map((s) => ({ label: s.label, color: s.color, value: fmt(s.values[hover] ?? 0) }))}
            />
          ) : null}
          <Legend items={series.map((s) => ({ label: s.label, color: s.color }))} />
        </>
      ) : null}
    </div>
  );
}

export interface TrendSeries {
  label: string;
  color: string;
  values: number[];
}

/**
 * กราฟแนวโน้มหลายเส้น + พื้นที่ใต้เส้น ชี้เมาส์แล้วเห็นค่าทุกเส้นของวันนั้น
 *
 * `integer` = ค่านับ (คำร้อง) แกน y จะไม่มีขั้นทศนิยม
 */
export function TrendChart({
  series,
  labels,
  emptyLabel,
  title,
  format,
  integer = false,
  height = 260,
  tooltipTitle,
}: {
  series: TrendSeries[];
  labels: string[];
  emptyLabel: string;
  title: string;
  format?: (v: number) => string;
  integer?: boolean;
  height?: number;
  /** หัวของ tooltip (ค่าเริ่มต้น = ป้ายแกน x) */
  tooltipTitle?: (index: number) => string;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const n = labels.length;
  const max = Math.max(0, ...series.flatMap((s) => s.values));
  const empty = n < 2 || max === 0;
  const fmt = format ?? ((v: number) => v.toLocaleString("lo-LA"));

  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = height - PAD.top - PAD.bottom;
  const { top, ticks } = niceScale(max, integer);
  const step = n > 1 ? plotW / (n - 1) : 0;
  const xAt = (i: number) => PAD.left + i * step;
  const yAt = (v: number) => PAD.top + plotH - (v / top) * plotH;
  const every = xLabelEvery(step);

  function onMove(e: MouseEvent<SVGSVGElement>) {
    const box = e.currentTarget.getBoundingClientRect();
    const i = Math.round((e.clientX - box.left - PAD.left) / Math.max(step, 1));
    setHover(Math.min(n - 1, Math.max(0, i)));
  }

  return (
    <div ref={ref} className="chart-box">
      {empty ? (
        <Empty label={emptyLabel} />
      ) : width > 0 ? (
        <>
          <svg
            // key เปลี่ยนตามข้อมูล → เส้นวาดใหม่ทุกครั้งที่เปลี่ยนช่วงเวลา
            key={`${String(n)}-${labels[0] ?? ""}-${series.map((s) => s.label).join()}`}
            className="chart"
            width={width}
            height={height}
            role="img"
            aria-label={title}
            onMouseMove={onMove}
            onMouseLeave={() => setHover(null)}
          >
            <YAxis ticks={ticks} top={top} width={width} plotH={plotH} format={integer ? (v) => v.toLocaleString("lo-LA") : axisNumber} />
            {series.map((s) => {
              const pts = s.values.map((v, i) => `${String(xAt(i))},${String(yAt(v))}`);
              const line = `M${pts.join(" L")}`;
              const area = `${line} L${String(xAt(n - 1))},${String(yAt(0))} L${String(xAt(0))},${String(yAt(0))} Z`;
              return (
                <g key={s.label}>
                  <path className="chart__area" d={area} fill={s.color} />
                  <path className="chart__line" d={line} stroke={s.color} pathLength={1} />
                </g>
              );
            })}
            {labels.map((label, i) =>
              i % every === 0 || i === n - 1 ? (
                // ป้ายสุดท้ายเสมอ แต่ถ้าชนกับป้ายก่อนหน้าก็ข้าม
                i === n - 1 && i % every !== 0 && (i % every) * step < 40 ? null : (
                  <text
                    key={label + String(i)}
                    className="chart__axis-label"
                    x={xAt(i)}
                    y={height - 8}
                    textAnchor={i === 0 ? "start" : i === n - 1 ? "end" : "middle"}
                  >
                    {label}
                  </text>
                )
              ) : null,
            )}
            {hover !== null ? (
              <g>
                <line className="chart__crosshair" x1={xAt(hover)} x2={xAt(hover)} y1={PAD.top} y2={PAD.top + plotH} />
                {series.map((s) => (
                  <circle key={s.label} className="chart__dot" cx={xAt(hover)} cy={yAt(s.values[hover] ?? 0)} r={4.5} stroke={s.color} />
                ))}
              </g>
            ) : null}
          </svg>
          {hover !== null ? (
            <Tooltip
              x={xAt(hover)}
              width={width}
              title={tooltipTitle ? tooltipTitle(hover) : (labels[hover] ?? "")}
              rows={series.map((s) => ({ label: s.label, color: s.color, value: fmt(s.values[hover] ?? 0) }))}
            />
          ) : null}
          {series.length > 1 ? <Legend items={series.map((s) => ({ label: s.label, color: s.color }))} /> : null}
        </>
      ) : null}
    </div>
  );
}

/** กราฟเส้นเส้นเดียว (จอ ລາຍງານລາຍຮັບ) — ตัวห่อของ TrendChart */
export function LineChart({
  values,
  labels,
  emptyLabel,
  title,
  format,
}: {
  values: number[];
  labels: string[];
  emptyLabel: string;
  title: string;
  format?: (v: number) => string;
}) {
  return (
    <TrendChart
      series={[{ label: title, color: "var(--navy-700)", values }]}
      labels={labels}
      emptyLabel={emptyLabel}
      title={title}
      format={format}
      height={220}
    />
  );
}

export interface DonutSlice {
  label: string;
  value: number;
  color: string;
}

/**
 * โดนัท + รายการข้างกราฟ — กลางวงบอก "ทั้งหมด" (ชี้ส่วนไหนก็เปลี่ยนเป็นส่วนนั้น)
 *
 * เดิมกลางวงโชว์ % ของส่วนที่ใหญ่สุดโดยไม่บอกว่าเป็นของอะไร ผู้ใช้อ่านไม่ออก
 */
export function DonutChart({
  slices,
  emptyLabel,
  title,
  totalLabel = "ທັງໝົດ",
  centerValue,
  legend = true,
}: {
  slices: DonutSlice[];
  emptyLabel: string;
  title: string;
  totalLabel?: string;
  /** ข้อความกลางวงแทนผลรวม (เช่น เงินแบบย่อ) */
  centerValue?: string;
  /** false = ไม่วาดรายการข้างวง เมื่อการ์ดมีรายการจัดอันดับของตัวเองอยู่แล้ว */
  legend?: boolean;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const total = slices.reduce((sum, s) => sum + s.value, 0);
  if (total === 0) return <Empty label={emptyLabel} />;

  const r = 64;
  const circumference = 2 * Math.PI * r;
  const pct = (v: number) => Math.round((v / total) * 100);
  // จุดเริ่มของแต่ละส่วน = ผลรวมของส่วนก่อนหน้า คำนวณแบบไม่มีตัวแปรสะสม
  // (ตัวแปรที่ถูกเขียนทับระหว่าง render ให้ผลไม่คงที่เมื่อ React วาดซ้ำ)
  const arcs = slices.map((slice, i) => ({
    slice,
    length: (slice.value / total) * circumference,
    offset: (slices.slice(0, i).reduce((sum, s) => sum + s.value, 0) / total) * circumference,
  }));
  const focused = slices.find((s) => s.label === hover);

  return (
    <div className="donut">
      <svg className="donut__svg" viewBox="0 0 180 180" role="img" aria-label={title} onMouseLeave={() => setHover(null)}>
        <circle cx={90} cy={90} r={r} fill="none" stroke="var(--bg-sunk)" strokeWidth={20} />
        <g transform="rotate(-90 90 90)">
          {arcs.map(({ slice, length, offset }) => {
            const dash = `${String(Math.max(length - 1.5, 0.5))} ${String(circumference)}`;
            return (
              <circle
                key={slice.label}
                className={hover && hover !== slice.label ? "donut__arc is-dim" : "donut__arc"}
                cx={90}
                cy={90}
                r={r}
                fill="none"
                stroke={slice.color}
                strokeWidth={hover === slice.label ? 24 : 20}
                strokeDasharray={dash}
                strokeDashoffset={-offset}
                onMouseEnter={() => setHover(slice.label)}
              >
                {REDUCED_MOTION ? null : (
                  <animate attributeName="stroke-dasharray" from={`0 ${String(circumference)}`} to={dash} dur="0.8s" fill="freeze" calcMode="spline" keySplines="0.2 0.8 0.2 1" keyTimes="0;1" />
                )}
              </circle>
            );
          })}
        </g>
        <text className="donut__value" x={90} y={90} textAnchor="middle">
          {focused ? `${String(pct(focused.value))}%` : (centerValue ?? total.toLocaleString("lo-LA"))}
        </text>
        <text className="donut__caption" x={90} y={110} textAnchor="middle">
          {focused ? focused.label : totalLabel}
        </text>
      </svg>
      {legend ? (
      <ul className="donut__legend">
        {slices.map((s) => (
          <li
            key={s.label}
            className={hover === s.label ? "is-active" : undefined}
            onMouseEnter={() => setHover(s.label)}
            onMouseLeave={() => setHover(null)}
          >
            <span className="legend__swatch" style={{ background: s.color }} />
            <span className="donut__legend-label">{s.label}</span>
            <span className="donut__legend-value num">{s.value.toLocaleString("lo-LA")}</span>
            <span className="donut__legend-pct num">{pct(s.value)}%</span>
          </li>
        ))}
      </ul>
      ) : null}
    </div>
  );
}

export interface RankItem {
  key: string;
  label: string;
  value: number;
  /** ข้อความรองใต้ป้าย เช่น "ອອກແລ້ວ 3" */
  sub?: string;
}

/** อันดับแบบแถบแนวนอน — แถบยาวตามสัดส่วนของอันดับหนึ่ง */
export function RankBars({
  items,
  emptyLabel,
  format,
  color = "var(--navy-600)",
}: {
  items: RankItem[];
  emptyLabel: string;
  format?: (v: number) => string;
  color?: string;
}) {
  const max = Math.max(0, ...items.map((i) => i.value));
  if (items.length === 0 || max === 0) return <Empty label={emptyLabel} />;
  const fmt = format ?? ((v: number) => v.toLocaleString("lo-LA"));
  return (
    <ol className="rank">
      {items.map((item, i) => (
        <li key={item.key} className="rank__row">
          <span className="rank__no num">{i + 1}</span>
          <span className="rank__body">
            <span className="rank__head">
              <span className="rank__label" title={item.label}>
                {item.label}
              </span>
              <span className="rank__value num">{fmt(item.value)}</span>
            </span>
            <span className="rank__track">
              <span
                className="rank__bar"
                style={{ width: `${String((item.value / max) * 100)}%`, background: color, animationDelay: `${String(i * 60)}ms` }}
              />
            </span>
            {item.sub ? <span className="rank__sub">{item.sub}</span> : null}
          </span>
        </li>
      ))}
    </ol>
  );
}

/** เส้นเล็กไม่มีแกน ใช้แทรกในการ์ด */
export function Sparkline({ values, title, color = "var(--navy-600)" }: { values: number[]; title: string; color?: string }) {
  const W = 96;
  const H = 28;
  const max = Math.max(0, ...values);
  if (values.length < 2 || max === 0) return null;
  const step = W / (values.length - 1);
  const pts = values.map((v, i) => `${String(i * step)},${String(H - 2 - (v / max) * (H - 4))}`);
  return (
    <svg className="sparkline" viewBox={`0 0 ${String(W)} ${String(H)}`} width={W} height={H} role="img" aria-label={title}>
      <path className="chart__area" d={`M0,${String(H)} L${pts.join(" L")} L${String(W)},${String(H)} Z`} fill={color} />
      <path className="chart__line chart__line--thin" d={`M${pts.join(" L")}`} stroke={color} pathLength={1} />
    </svg>
  );
}

/** ตัวเลขนับขึ้นตอนค่าเปลี่ยน (0.7 วิ) — คนลดการเคลื่อนไหวเห็นค่าสุดท้ายทันที */
export function CountUp({ value, format }: { value: number; format: (v: number) => string }): ReactNode {
  const [shown, setShown] = useState(REDUCED_MOTION ? value : 0);
  const from = useRef(REDUCED_MOTION ? value : 0);
  useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const a = from.current;
    const tick = (t: number) => {
      const p = REDUCED_MOTION ? 1 : Math.min(1, (t - start) / 700);
      const v = a + (value - a) * (1 - (1 - p) ** 3);
      from.current = v;
      setShown(v);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return format(Math.round(shown));
}

function Legend({ items }: { items: { label: string; color: string }[] }) {
  return (
    <div className="legend">
      {items.map((item) => (
        <span key={item.label}>
          <span className="legend__swatch" style={{ background: item.color }} />
          {item.label}
        </span>
      ))}
    </div>
  );
}
